/**
 * DeliveryReliabilityPanel — supplier dossier "Delivery" section
 * (Lane 295, plan Lanes 2 + 5).
 *
 * Two blocks over the committed delivery-reliability engine (Lane 291), fed by
 * GET /api/suppliers/{id}/delivery-reliability — the single home for both the
 * slippage SQL and the actual-lead-time SQL, so a dossier number can never
 * disagree with the chat / deck surfaces built on the same engine:
 *
 *   1. DELIVERY RELIABILITY (slippage) — does this supplier deliver by the date
 *      it PROMISED? on-time %, avg days late WHEN LATE (the headline), and the
 *      leverage figure ($ arrived late), with the MANDATORY promise-date coverage
 *      label (a % is never shown without "based on N of M received POs that carry
 *      a promise date" — plan §6) + a category drill-down. It is its OWN
 *      supplier-facing performance dimension: NEVER folded into the grade or the
 *      scorecard (plan lock 1) — it sits BESIDE them in its own section.
 *   2. ACTUAL LEAD TIME (reframe) — order-to-receipt time by category, WITHOUT
 *      the padded/understated claim (XMEK has no quoted-lead-time baseline — plan
 *      §4.3/§6). The per-material variance vs a displayed baseline lights up only
 *      where a customer has quoted lead times (absent for XMEK, present-or-absent
 *      by design).
 *
 * Double OFF-lock (the LeadtimeReliabilityPanel / OpportunityCallout pattern):
 * reads `deliveryReliabilityEnabled` from AuthContext and bails (no fetch,
 * renders null) when the env flag is off; the backend endpoint independently
 * gates on the SAME env var. Fetch errors render null — the dossier must never
 * break on this panel. Each engine block fails CLOSED to an honest empty state,
 * never a fabricated number.
 *
 * Placement (Lane 295 decision): its OWN "Delivery" explore chip (NOT nested in
 * the scorecard-gated Performance tab like the Lane-156 lead-time panel), so
 * this flagship dimension is prominent and gated ONLY on
 * DELIVERY_RELIABILITY_ENABLED — it is independent of the scorecard UI (plan
 * lock 2: "needs to permeate everywhere"). When this flag is on, the reframe
 * SUPERSEDES the Lane-156 padded/understated LeadtimeReliabilityPanel (that
 * panel steps aside in SupplierDetail) so lead time is never shown twice.
 *
 * Lane 215 (Linear rebuild) chrome: reads the `.sd` Linear token layer (panel,
 * border, hair, the text tiers, good/warn/crit) so it flips with the shell.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import { PoDeliveryFigures, PoDeliveryBands } from './PoDeliveryChanges';

// Linear panel chrome (mirrors LeadtimeReliabilityPanel's LINEAR_PANEL).
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

const fmtUsd = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(Number(v) || 0);

// Compact $ for the headline tile (readability); full $ in the tables (precision).
const fmtUsdCompact = (v) => {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
};

const fmtPct = (v) => (v === null || v === undefined ? 'Not available' : `${Math.round(v * 100)}%`);

// Days late WHEN LATE is >= 0 by construction; net can be negative (early).
// These two stay "d"-suffixed -- they belong to the SLIPPAGE (delivery
// reliability) block, a different metric family from "actual lead time"
// (QA-3 item 3, Mega QA 2026-07-22, scopes the "d"-suffix drop to lead-time
// numbers only).
const fmtLateDays = (v) => (v === null || v === undefined ? 'Not available' : `+${Math.round(v)}d`);
const fmtNetDays = (v) =>
  v === null || v === undefined ? 'Not available' : `${v >= 0 ? '+' : ''}${Math.round(v)}d`;
// QA-3 item 3: the "Actual lead time" block's numbers drop the "d" unit
// suffix -- the block's own header now carries the unit ("Actual lead time
// (days)"), so every number under it is a bare figure (kept "~" to signal
// these are averages, per the ask's literal "d" scope).
const fmtLtDays = (v) => (v === null || v === undefined ? 'Not available' : `~${Math.round(v)}`);
// The per-material Variance column (also "actual lead time" territory) keeps
// its sign -- direction is real information, distinct from a unit suffix --
// but drops "d" the same way. A dedicated formatter so the shared fmtNetDays
// above (used by the UNRELATED slippage "Net vs promise" line) stays "d"-
// suffixed and untouched.
const fmtVarianceDays = (v) =>
  v === null || v === undefined ? 'Not available' : `${v >= 0 ? '+' : ''}${Math.round(v)}`;

const headerCell = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--text-3)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--letter-spacing-wide)',
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--border)',
};

const bodyCell = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  padding: '6px 8px',
  borderBottom: '1px solid var(--hair)',
};

const blockTitle = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--text-3)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--letter-spacing-wide)',
  marginBottom: '4px',
};

const blockSub = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-3)',
  marginBottom: '10px',
};

const emptyNote = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-3)',
  margin: 0,
};

const tile = {
  flex: '1 1 0',
  minWidth: '90px',
  padding: '10px 12px',
  borderRadius: '8px',
  background: 'var(--panel-2)',
  border: '1px solid var(--hair)',
};
const tileLabel = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-3)',
  marginBottom: '3px',
};
const tileValue = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--text)',
};

function windowLabel(months) {
  return months ? `last ${months} months` : 'recent receipts';
}

// Lane 382 item 4: the "Net vs promise" tile's direction — the ONE decision its
// colour and its word both read from, so the two can never disagree. Net is the
// only slippage figure whose sign carries meaning: negative is EARLY, which is
// genuinely good and must not render as a warning. A missing figure is
// 'unknown': em dash, no colour claim, no direction word (never a fabricated
// "on promise").
function netDirection(v) {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return 'unknown';
  if (v < 0) return 'early';
  if (v > 0) return 'late';
  return 'on-promise';
}
const NET_DIRECTION_COLOR = {
  early: 'var(--good)',
  late: 'var(--warn)',
  'on-promise': 'var(--text)',
  unknown: 'var(--text)',
};
const NET_DIRECTION_WORDS = {
  early: 'early on average',
  late: 'late on average',
  'on-promise': 'on promise on average',
  unknown: null,
};

// ---- Slippage block ------------------------------------------------------
function SlippageBlock({ slippage, poChanges }) {
  const months = slippage?.window_months;
  return (
    <div data-testid="delivery-slippage-block">
      <div style={blockTitle}>Delivery reliability</div>
      <div style={blockSub}>
        Did this supplier deliver by the date it promised, {windowLabel(months)}.
      </div>
      {slippage?.available === false || !slippage?.overall ? (
        <>
        <p data-testid="delivery-slippage-unavailable" style={emptyNote}>
          {slippage?.reason === 'no promise dates in this feed yet'
            ? 'No promise dates in your PO feed yet. Delivery reliability will appear here once your purchase orders carry a promise date.'
            : (slippage?.reason
                || 'No promise-dated orders for this supplier in the window.')}
        </p>
        <PoDeliveryFigures data={poChanges} />
        <PoDeliveryBands data={poChanges} />
        </>
      ) : (
        <SlippageBody slippage={slippage} poChanges={poChanges} />
      )}
    </div>
  );
}

function SlippageBody({ slippage, poChanges }) {
  const o = slippage.overall;
  const cats = slippage.categories || [];
  const worst = slippage.worst_late_pos || [];
  return (
    <>
      {/* LANE 382 item 4 — FOUR tiles, and "Net vs promise" sits next to On-time.
          Ryan (staging QA 2026-07-25): "next to on-time should be the net versus
          promise. Right now you have it as a write-up of approximately twelve days
          early on average, and then the next one is average late when late — just
          have two separate cards, that way we can see the full picture, because
          for these guys on average they're early, but there are some that are
          late."

          The two averages answer different questions and the pair IS the picture:
          NET vs promise says how the supplier runs overall (SKF: ~12 days EARLY),
          AVG LATE WHEN LATE says how bad the misses are when they happen. Net was
          buried mid-sentence in the coverage line below, so a reader comparing
          "early on average" against "+N days late" had to read prose against a
          tile. Both are tiles now, side by side, both promoted from the same
          `overall` object -- no new figure, no recomputation.

          Net keeps its own colour rule: EARLY (negative) is genuinely good, so it
          reads --good; late-on-average reads --warn; it is the one tile here whose
          sign carries direction. */}
      <div data-testid="delivery-slippage-tiles" style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div style={tile}>
          <div style={tileLabel}>On-time</div>
          <div style={tileValue}>{fmtPct(o.pct_on_time)}</div>
        </div>
        {/* `data-net-direction` carries the direction the colour encodes:
            jsdom drops `var()` colours out of the style attribute, so a colour
            assertion cannot be written against the rendered DOM -- this
            attribute is what the pin reads, and it is the same decision. */}
        <div
          style={tile}
          data-testid="delivery-tile-net-vs-promise"
          data-net-direction={netDirection(o.avg_days_vs_promise_net)}
        >
          <div style={tileLabel}>Net vs promise</div>
          <div style={{ ...tileValue, color: NET_DIRECTION_COLOR[netDirection(o.avg_days_vs_promise_net)] }}>
            {fmtNetDays(o.avg_days_vs_promise_net)}
          </div>
          {NET_DIRECTION_WORDS[netDirection(o.avg_days_vs_promise_net)] && (
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-3)', marginTop: '2px' }}>
              {NET_DIRECTION_WORDS[netDirection(o.avg_days_vs_promise_net)]}
            </div>
          )}
        </div>
        <div style={tile}>
          <div style={tileLabel}>Avg late (when late)</div>
          <div style={{ ...tileValue, color: o.avg_days_late_when_late ? 'var(--warn)' : 'var(--text)' }}>
            {fmtLateDays(o.avg_days_late_when_late)}
          </div>
        </div>
        <div style={tile}>
          <div style={tileLabel}>$ arrived late</div>
          <div style={{ ...tileValue, color: o.late_spend > 0 ? 'var(--warn)' : 'var(--text)' }}>
            {fmtUsdCompact(o.late_spend)}
          </div>
        </div>
      </div>
      {/* Item 4: the coverage line keeps ONLY the mandatory promise-date coverage
          label (plan §6) -- the "Net vs promise ... (early on average)" clause it
          used to carry is now the tile above, and restating it here would put the
          same figure on the page twice. */}
      <div data-testid="delivery-slippage-coverage" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-3)', marginBottom: '4px' }}>
        {o.coverage_label}.
      </div>

      {cats.length > 0 && (
        <table data-testid="delivery-slippage-categories" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
          <thead>
            <tr>
              <th style={headerCell}>Category</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>On-time</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Late (when late)</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>$ late</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.category}>
                <td style={bodyCell}>{c.category_label || c.category}</td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtPct(c.pct_on_time)}</td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtLateDays(c.avg_days_late_when_late)}</td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtUsd(c.late_spend)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Ryan's order: tiles, categories, the three PO boxes, then the
          one-row "how far dates moved" table, then the largest late orders. */}
      <PoDeliveryFigures data={poChanges} />
      <PoDeliveryBands data={poChanges} />

      {worst.length > 0 && (
        <div data-testid="delivery-slippage-worst" style={{ marginTop: '10px' }}>
          <div style={{ ...blockTitle, marginBottom: '6px' }}>Largest late orders</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCell}>Material</th>
                <th style={{ ...headerCell, textAlign: 'right' }}>Days late</th>
                <th style={{ ...headerCell, textAlign: 'right' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {worst.map((w) => (
                <tr key={`${w.material_number}-${w.receipt_date}`}>
                  <td style={bodyCell}>
                    <span style={{ fontWeight: 'var(--font-weight-medium)' }}>{w.material_number}</span>
                    {w.material_name && (
                      <span style={{ color: 'var(--text-3)', fontSize: 'var(--font-size-xs)', marginLeft: '6px' }}>
                        {w.material_name}
                      </span>
                    )}
                  </td>
                  <td style={{ ...bodyCell, textAlign: 'right', color: 'var(--warn)' }}>
                    {w.days_late === null || w.days_late === undefined ? 'Not available' : `+${w.days_late}d`}
                  </td>
                  <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtUsd(w.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---- Actual lead time block (reframe) ------------------------------------
function LeadtimeBlock({ leadtime }) {
  const months = leadtime?.window_months;
  return (
    <div data-testid="delivery-leadtime-block" style={{ marginTop: '18px' }}>
      {/* QA-3 item 3 (Mega QA 2026-07-22): the header carries the unit
          ("(days)") so every number underneath can be a bare figure. */}
      <div style={blockTitle}>Actual lead time (days)</div>
      <div style={blockSub}>
        How long from order to receipt, {windowLabel(months)}.
      </div>
      {leadtime?.available === false || !leadtime?.overall ? (
        <p data-testid="delivery-leadtime-unavailable" style={emptyNote}>
          {leadtime?.reason && leadtime.reason.startsWith('lead-time intelligence needs an order date')
            ? 'No real order-to-receipt data yet. Your PO history doesn’t carry a usable order date. Actual lead times will appear here once it does.'
            : (leadtime?.reason
                || 'No received orders with a real order date for this supplier in the window.')}
        </p>
      ) : (
        <LeadtimeBody leadtime={leadtime} />
      )}
    </div>
  );
}

function LeadtimeBody({ leadtime }) {
  const o = leadtime.overall;
  const cats = leadtime.categories || [];
  const materials = leadtime.materials || [];
  const withBaseline = materials.filter(
    (m) => m.displayed_lt_days !== null && m.displayed_lt_days !== undefined,
  );
  const range =
    o.min_lt_days !== null && o.min_lt_days !== undefined &&
    o.max_lt_days !== null && o.max_lt_days !== undefined
      ? ` (range ${Math.round(o.min_lt_days)} to ${Math.round(o.max_lt_days)}, ${o.po_count} receipt${o.po_count !== 1 ? 's' : ''})`
      : '';
  return (
    <>
      <div data-testid="delivery-leadtime-overall" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-2)', marginBottom: '8px' }}>
        <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--text)' }}>{fmtLtDays(o.actual_avg_lt_days)}</span>
        {' '}average order-to-receipt{range}.
      </div>

      {cats.length > 0 && (
        <table data-testid="delivery-leadtime-categories" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headerCell}>Category</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Actual LT</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Receipts</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Spend</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.category}>
                <td style={bodyCell}>{c.category_label || c.category}</td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtLtDays(c.actual_avg_lt_days)}</td>
                <td style={{ ...bodyCell, textAlign: 'right', color: 'var(--text-2)' }}>{c.po_count}</td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtUsd(c.spend)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Variance vs a quoted lead time — present ONLY where a customer has
          quoted baselines (absent for XMEK). No padded/understated framing:
          the raw variance number, worded neutrally. */}
      {withBaseline.length > 0 && (
        <div data-testid="delivery-leadtime-variance" style={{ marginTop: '10px' }}>
          <div style={{ ...blockTitle, marginBottom: '6px' }}>Actual vs your quoted lead time</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCell}>Material</th>
                <th style={{ ...headerCell, textAlign: 'right' }}>Quoted</th>
                <th style={{ ...headerCell, textAlign: 'right' }}>Actual</th>
                <th style={{ ...headerCell, textAlign: 'right' }}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {withBaseline.map((m) => (
                <tr key={m.material_number}>
                  <td style={bodyCell}>
                    <span style={{ fontWeight: 'var(--font-weight-medium)' }}>{m.material_number}</span>
                    {m.material_name && (
                      <span style={{ color: 'var(--text-3)', fontSize: 'var(--font-size-xs)', marginLeft: '6px' }}>
                        {m.material_name}
                      </span>
                    )}
                  </td>
                  <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtLtDays(m.displayed_lt_days)}</td>
                  <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtLtDays(m.actual_avg_lt_days)}</td>
                  <td style={{ ...bodyCell, textAlign: 'right', color: m.variance_days > 0 ? 'var(--warn)' : 'var(--text-2)' }}>
                    {fmtVarianceDays(m.variance_days)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function DeliveryReliabilityPanel({ supplierId, token }) {
  const { deliveryReliabilityEnabled } = useAuth();
  const [data, setData] = useState(null);
  const [poChanges, setPoChanges] = useState(null);

  const skip = !deliveryReliabilityEnabled || !supplierId || !token;

  useEffect(() => {
    if (skip) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/suppliers/${encodeURIComponent(supplierId)}/delivery-reliability`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => { cancelled = true; };
  }, [supplierId, token, deliveryReliabilityEnabled, skip]);

  // The PO date-change section has its own request (it takes seconds), fetched
  // only after the figures above have rendered and only when the server says
  // there is loaded PO history. A failure just leaves the section out.
  const poDeferred = Boolean(data && data.po_date_changes_deferred);
  useEffect(() => {
    setPoChanges(null);
    if (skip || !poDeferred) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/suppliers/${encodeURIComponent(supplierId)}/delivery-reliability/po-date-changes`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled) setPoChanges(body ? body.po_date_changes : null);
      })
      .catch(() => {
        if (!cancelled) setPoChanges(null);
      });
    return () => { cancelled = true; };
  }, [supplierId, token, skip, poDeferred]);

  // Double OFF-lock + lazy-resolution no-op + quiet failure: the section never
  // breaks (or changes at all) because of this panel.
  if (skip || !data || data.delivery_reliability_enabled === false) return null;

  return (
    <div style={{ ...LINEAR_PANEL, padding: '16px', marginTop: '16px' }}>
      <div data-testid="delivery-reliability-panel">
        <SlippageBlock slippage={data.slippage} poChanges={poChanges} />
        <LeadtimeBlock leadtime={data.leadtime} />
      </div>
    </div>
  );
}

export default DeliveryReliabilityPanel;
